import * as Joi from "joi";
import { useCallback, useMemo, useState } from "react";
import { Flex, Tabs } from "@radix-ui/themes";

import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { InteractionType } from "@/routes/simulator-live-mode/types";
import { Topic } from "@/v2/entities/topic/Topic.model";

import { useDraft } from "../../context/useDraft";
import { useSimulator } from "../../context/SimulatorWrapper";
import { useChat } from "../../context/ChatWrapper";
import styles from "../../styles.module.scss";

import {
  topicToTopicWithBadges,
  TopicWithBadges,
} from "./TopicsList.types";
import { TranscriptState } from "./TopicsList.shared.types";
import { TopicsTab } from "./TopicsTab";
import { SamplesTab } from "./SamplesTab";
import { TrainingTranscript } from "./TrainingTranscript";

interface NewLeftPanelProps {
  isLoadingMessages: boolean;
}

const panelTabs = ["topics", "samples"] as const;
type PanelTab = (typeof panelTabs)[number];

export const NewLeftPanel = ({ isLoadingMessages }: NewLeftPanelProps) => {
  const { simulator, topics: topicsList } = useSimulator();
  const { selectedTopic, handleTopicSelect } = useChat();

  const [activeTab, setActiveTab] = useState<PanelTab>("topics");

  const [showTranscript, setShowTranscript] = useState<TranscriptState | null>(
    null
  );

  const { topics } = useDraft();

  const topicsWithBadges: TopicWithBadges[] = useMemo(() => {
    return topics.map((topicStatus) => topicToTopicWithBadges(topicStatus));
  }, [topics]);

  const resetNavigation = useCallback(() => {
    setShowTranscript(null);
  }, []);

  const handleNavigateToTranscript = useCallback(
    (
      interactionId: string,
      fetchTranscriptId: string,
      interactionType: InteractionType,
      pendingReviewId?: number
    ) => {
      setShowTranscript({
        interactionId,
        fetchTranscriptId,
        interactionType,
        pendingReviewId,
      });
    },
    []
  );

  const [selectedTopicForSessions, setSelectedTopicForSessions] =
    useState<Topic | null>(null);

  const handleNavigateToTopicSessions = useCallback((topic: Topic) => {
    setSelectedTopicForSessions(topic);
  }, []);

  const handleBackFromSessions = useCallback(() => {
    setSelectedTopicForSessions(null);
  }, []);

  const handleTabChange = useCallback(
    (value: string) => {
      setActiveTab(
        Joi.string()
          .valid(...panelTabs)
          .validate(value).value
      );
    },
    []
  );

  return (
    <Flex direction="column" p={"2"} className={styles.topicsListContainer}>
      {!showTranscript ? (
        <Tabs.Root value={activeTab} onValueChange={handleTabChange}>
          {!selectedTopicForSessions && (
            <Tabs.List>
              <Tabs.Trigger value="topics">Topics</Tabs.Trigger>
              <Tabs.Trigger value="samples">Samples</Tabs.Trigger>
            </Tabs.List>
          )}

          <Tabs.Content value="topics">
            <TopicsTab
              topics={topicsWithBadges}
              selectedTopic={selectedTopic}
              onTopicSelect={handleTopicSelect}
              onNavigateToSessions={handleNavigateToTopicSessions}
              onNavigateToTranscript={handleNavigateToTranscript}
              topicsList={topicsList || undefined}
              isDisabled={isLoadingMessages}
              selectedTopicForSessions={selectedTopicForSessions}
              onBackFromSessions={handleBackFromSessions}
            />
          </Tabs.Content>

          <Tabs.Content value="samples">
            <SamplesTab
              selectedTopic={null}
              onNavigateToTranscript={handleNavigateToTranscript}
              onResetNavigation={resetNavigation}
              onBack={() => {}}
              showBackButton={false}
            />
          </Tabs.Content>
        </Tabs.Root>
      ) : (
        <TrainingTranscript
          showTranscript={showTranscript}
          handleBack={resetNavigation}
        />
      )}
    </Flex>
  );
};

